import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

/**
 * AssignWithBriefModal — shared dialog for any flow that hands a task /
 * content card to a teammate (Lens "Assign to…", Upcoming Occasions
 * "Create Content", Morning Intelligence triage routes).
 *
 * Props:
 *   open              : boolean
 *   onClose           : () => void
 *   onConfirm         : ({ assignee_email, brief_for_assignee }) => Promise<boolean>
 *                       Resolve true to close, false to keep open (e.g. validation failure).
 *   title             : string              — dialog heading ("Assign Lens card", etc.)
 *   subtitle          : string (optional)   — small description under the heading
 *   assignablePool    : Array<{ email, name, is_analyst?, role? }>
 *   currentAssignee   : string (optional)   — pre-select this email
 *   currentBrief      : string (optional)   — pre-fill the textarea (edit-mode)
 *   suggestedBrief    : string (optional)   — placeholder hint; used when currentBrief is empty
 *   currentUserEmail  : string (optional)   — to flag (me) in the picker
 *   confirmLabel      : string (optional, default "Assign")
 *   showAssigneePicker: boolean (default true) — for edit-only flows hide the picker
 */
export default function AssignWithBriefModal({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  assignablePool = [],
  currentAssignee = "",
  currentBrief = "",
  suggestedBrief = "",
  currentUserEmail = "",
  confirmLabel = "Assign",
  showAssigneePicker = true,
  pickerLabel = "Assign to",
  pickerHint = "★ marks formal analysts/admins (preferred routing).",
  briefLabel = "Brief for Assignee",
  briefHint = "Optional — explain the angle so they don't have to guess. E.g. tone, format, what to avoid.",
}) {
  const [assignee, setAssignee] = useState(currentAssignee);
  const [brief, setBrief] = useState(currentBrief);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAssignee(currentAssignee);
      // If there's already a brief on the card, keep it. Otherwise prefill
      // with the suggested text so the user can confirm or edit.
      setBrief(currentBrief || suggestedBrief || "");
    }
  }, [open, currentAssignee, currentBrief, suggestedBrief]);

  const handleConfirm = async () => {
    if (showAssigneePicker && !assignee) {
      // Allow the brief-only edit mode to skip the picker; otherwise need an assignee.
      return;
    }
    setSaving(true);
    const ok = await onConfirm({
      assignee_email: assignee || null,
      brief_for_assignee: brief.trim(),
    });
    setSaving(false);
    if (ok) onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="assign-with-brief-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {showAssigneePicker && (
            <div>
              <label className="text-sm font-medium text-slate-700" htmlFor="assign-brief-picker">
                {pickerLabel}
              </label>
              <p className="text-xs text-slate-500 mb-2">
                {pickerHint}
              </p>
              <select
                id="assign-brief-picker"
                data-testid="assign-brief-picker"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="w-full h-9 px-2 text-sm bg-white border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 outline-none"
              >
                <option value="">Choose a teammate…</option>
                {(() => {
                  // Disambiguate display when two users share the same name —
                  // append the email so the picker stays unambiguous.
                  const nameCounts = assignablePool.reduce((acc, a) => {
                    const k = (a.name || a.email.split("@")[0]).trim().toLowerCase();
                    acc[k] = (acc[k] || 0) + 1;
                    return acc;
                  }, {});
                  return assignablePool.map((a) => {
                    const display = a.name || a.email.split("@")[0];
                    const key = display.trim().toLowerCase();
                    const me = a.email === currentUserEmail ? " (me)" : "";
                    const star = a.is_analyst ? " ★" : "";
                    const tail = nameCounts[key] > 1 ? ` <${a.email}>` : "";
                    return (
                      <option key={a.email} value={a.email}>
                        {display + star + me + tail}
                      </option>
                    );
                  });
                })()}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="assign-brief-text">
              {briefLabel}
            </label>
            <p className="text-xs text-slate-500 mb-2">
              {briefHint}
            </p>
            <Textarea
              id="assign-brief-text"
              data-testid="assign-brief-text"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder={suggestedBrief || "e.g. Ghana's Republic Day — celebratory tone, do NOT reference our product."}
              rows={5}
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="assign-brief-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || (showAssigneePicker && !assignee)}
            data-testid="assign-brief-confirm"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Helper: build a Rex-style suggested brief for an Upcoming Occasion row.
 * Pure function — easy to unit-test, reused by MorningScrumOS.js and any
 * future "occasion → assign" surface.
 */
export function buildOccasionBrief(occ) {
  if (!occ) return "";
  const country = (occ.country || "").trim();
  const title = (occ.title || "").trim();
  const day = occ._dt
    ? occ._dt.toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : (occ.date || "").slice(0, 10);
  const type = (occ.type || "").toLowerCase();

  // Heuristics tuned for the most common occasion families.
  let tone = "celebratory";
  let directive = "Keep it human and product-neutral — no CTA, no pricing.";
  if (type.includes("memorial") || type.includes("remembrance")) {
    tone = "respectful";
    directive = "Sombre tone — acknowledge without commercial framing.";
  } else if (type.includes("religious")) {
    tone = "warm and inclusive";
    directive = "Sensitive tone — universal greeting, avoid sectarian language.";
  } else if (type.includes("awareness")) {
    tone = "informative";
    directive = "Lead with the cause/issue, not us.";
  }

  const parts = [];
  if (country) parts.push(`${country}'s`);
  parts.push(title || "occasion");
  if (day) parts.push(`· ${day}`);
  parts.push(`· ${tone} tone`);
  return `${parts.join(" ")}. ${directive}`;
}

/**
 * Helper: derive a starter brief for a Lens task spawned by
 * ``routeToLensSmart``. Looks at the triage_action + source context
 * (headline, company, country, importance/pressure) and produces a
 * tone-tuned prompt the assigner can edit or accept.
 *
 * Pure function — keep this dumb and predictable.
 */
export function buildBriefFromPayload(payload) {
  if (!payload) return "";
  const sp = payload.source_payload || {};
  const headline = (sp.headline || payload.title || "").trim();
  const company = (sp.company || "").trim();
  const country = (sp.country || "").trim();
  const pressure = (sp.importance || sp.reason_for_priority || sp.key_pressure || "").trim();
  const action = (payload.triage_action || "").toLowerCase();
  const sourceKind = (sp.source_kind || (payload.source_news_id ? "news" : "")).toLowerCase();

  // Context line: only prefix with `Re: "<headline>"` when the source is
  // an actual news headline. For Top Commercial Moves / At-Risk clients
  // / Replenishment items, lead with the company instead — saying
  // `Re: "Research & add to CRM"` reads as nonsense.
  const isNews = sourceKind === "news" || !!payload.source_news_id;
  const ctxParts = [];
  if (isNews && headline) ctxParts.push(`Re: "${headline}"`);
  if (company) ctxParts.push(`${ctxParts.length ? "· " : ""}${company}`);
  if (country) ctxParts.push(`${ctxParts.length ? "· " : ""}${country}`);
  const ctxLine = ctxParts.join(" ");
  const pressureLine = pressure ? `\nWhy now: ${pressure}` : "";

  if (action === "research_crm") {
    return `${ctxLine}${pressureLine}\nAngle: dig into ${company || "the org"}'s commercial fit — decision-makers, current vendors, recent moves. Note what would unlock a conversation.`;
  }
  if (action === "add_to_pipeline") {
    return `${ctxLine}${pressureLine}\nAngle: confirm/adjust the AI-estimated deal value, set stage, and note the access path.`;
  }
  if (action === "conversation_entry") {
    return `${ctxLine}${pressureLine}\nAngle: peer-to-peer tone — open with the news, lead with curiosity not a pitch. Suggest a follow-up if they bite.`;
  }
  if (action === "create_content" || action === "authority_content") {
    return `${ctxLine}${pressureLine}\nAngle: LinkedIn-first take. Lead with the implication, not the press release. Avoid product mentions.`;
  }
  if (action === "brief_team") {
    return `${ctxLine}${pressureLine}\nAngle: 2-sentence summary for tomorrow's stand-up. What changed and what (if anything) we should do about it.`;
  }
  if (action === "track_only") {
    return `${ctxLine}${pressureLine}\nAngle: note-to-self — what to watch for and when to re-surface.`;
  }
  // Fallback — generic.
  return `${ctxLine}${pressureLine}\nAngle: tone, format, what to avoid…`;
}
